import { ArrowRight } from "@tamagui/lucide-icons";
import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import { ScrollView, View, getTokenValue } from "tamagui";

import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { Chip, IconButton } from "@/components";
import Gradient from "@/components/Gradient";
import { useConversationComposition } from "@/store/conversation";

const SCROLL_AMOUNT_PER_CLICK = 250;
const SCROLL_ANIMATION_DURATION = 600;
const SCROLL_END_THRESHOLD = 8;

export default function MultiselectInputControl({ ...props }) {
  const { sendNoriMessage } = useSendNoriMessage();
  const isPending = useIsChatMutationPending();
  const { inputControl, pressChip } = useConversationComposition();

  const [showGradientLeft, setShowGradientLeft] = useState(false);
  const [showGradientRight, setShowGradientRight] = useState(true);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const [currentScrollX, setCurrentScrollX] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);
  const isProgrammaticScrollRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const suggestions = inputControl?.suggestions ?? [];

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleScroll = ({ nativeEvent }: { nativeEvent: any }) => {
    const scrollX = nativeEvent.contentOffset.x;
    setCurrentScrollX(scrollX);

    setShowGradientLeft(scrollX > 0);

    if (scrollX > 0 && !isProgrammaticScrollRef.current) {
      setHasUserScrolled(true);
    }

    const isAtEnd =
      scrollX + nativeEvent.layoutMeasurement.width >=
      nativeEvent.contentSize.width - SCROLL_END_THRESHOLD;
    setShowGradientRight(!isAtEnd);
  };

  const handleArrowPress = () => {
    if (!scrollViewRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Mark as programmatic scroll so we don't hide the button
    isProgrammaticScrollRef.current = true;
    const scrollAmount = currentScrollX + SCROLL_AMOUNT_PER_CLICK;
    scrollViewRef.current.scrollTo({
      x: scrollAmount,
      animated: true,
    });

    timeoutRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      timeoutRef.current = null;
    }, SCROLL_ANIMATION_DURATION);
  };

  if (inputControl?.type !== "multiple_choice") return null;
  return (
    <View data-purpose="anchor-suggestions" marginVertical="$200" {...props}>
      <View position="relative">
        <ScrollView
          ref={scrollViewRef}
          horizontal
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: getTokenValue("$100"),
          }}
          onScroll={handleScroll}
        >
          {suggestions.map((suggestion) => (
            <Chip
              key={suggestion}
              text={suggestion}
              onPress={(e) => {
                // Don't bubble event up to the ChatInput's container, which would refocus input
                if (Platform.OS === "web") e.stopPropagation();
                if (suggestion.toLowerCase() === "none") {
                  return sendNoriMessage(suggestion);
                }
                pressChip(suggestion);
              }}
              disabled={isPending}
            />
          ))}
        </ScrollView>
        {showGradientLeft && <Gradient left={true} />}
        {showGradientRight && <Gradient left={false} />}
        {suggestions.length > 0 && showGradientRight && !hasUserScrolled && (
          <View
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            justifyContent="center"
            alignItems="center"
            paddingRight="$50"
            pointerEvents="box-none"
          >
            <IconButton
              Icon={ArrowRight}
              aria-label="Scroll right"
              onPress={handleArrowPress}
              backgroundColor="$fillInput"
              height="$400"
              width="$400"
              iconSize="$250"
            />
          </View>
        )}
      </View>
    </View>
  );
}
